// Leader Reports Management System
import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';

const LeaderReportsManagement = () => {
  const API_URL = process.env.REACT_APP_API_URL;
  if (!API_URL) {
    throw new Error('API URL not configured');
  }

  const [activeView, setActiveView] = useState('dashboard');
  const [stats, setStats] = useState(null);
  const [reports, setReports] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedReport, setSelectedReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    category: '',
    status: '',
    search: '',
    startDate: '',
    endDate: ''
  });
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0 });
  const [showReportModal, setShowReportModal] = useState(false);
  const [editingReport, setEditingReport] = useState(null); // null = create mode, object = edit mode
  const [deletingReport, setDeletingReport] = useState(null); // report to confirm delete
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    category_id: '',
    activity_date: '',
    content: '',
    tags: [],
    status: 'draft'
  });

  const getToken = () => localStorage.getItem('adminToken');

  useEffect(() => {
    fetchDashboardStats();
    fetchCategories();
    fetchReports();
  }, []);

  useEffect(() => {
    if (activeView === 'reports') {
      fetchReports();
    }
  }, [filters, pagination.page]);

  // ==================== API CALLS ====================

  const fetchDashboardStats = async () => {
    try {
      const token = getToken();
      if (!token) return;

      const response = await fetch(`${API_URL}/leader-reports/dashboard/stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setStats(data.data);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchCategories = async () => {
    try {
      const token = getToken();
      if (!token) return;

      const response = await fetch(`${API_URL}/leader-reports/categories`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setCategories(data.data);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchReports = async () => {
    try {
      setLoading(true);
      const token = getToken();
      if (!token) return;

      const queryParams = new URLSearchParams({
        page: pagination.page,
        limit: pagination.limit,
        ...filters
      });

      const response = await fetch(`${API_URL}/leader-reports/reports?${queryParams}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setReports(data.data);
        setPagination(prev => ({ ...prev, total: data.pagination.total }));
      }
    } catch (error) {
      console.error('Error fetching reports:', error);
      toast.error('Failed to fetch reports');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateReport = async () => {
    try {
      const token = getToken();
      if (!token) return;

      const response = await fetch(`${API_URL}/leader-reports/reports`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (data.success) {
        toast.success(data.message);
        setShowReportModal(false);
        resetForm();
        fetchReports();
        fetchDashboardStats();
      } else {
        toast.error(data.message);
      }
    } catch (error) {
      console.error('Error creating report:', error);
      toast.error('Failed to create report');
    }
  };

  const handleUpdateStatus = async (reportId, status, comment = '') => {
    try {
      const token = getToken();
      if (!token) return;

      const response = await fetch(`${API_URL}/leader-reports/reports/${reportId}/status`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status, comment })
      });

      const data = await response.json();

      if (data.success) {
        toast.success(data.message);
        fetchReports();
        if (selectedReport?.id === reportId) {
          viewReport(reportId);
        }
      } else {
        toast.error(data.message);
      }
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Failed to update status');
    }
  };

  const viewReport = async (reportId) => {
    try {
      const token = getToken();
      if (!token) return;

      const response = await fetch(`${API_URL}/leader-reports/reports/${reportId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setSelectedReport(data.data);
        setActiveView('view-report');
      }
    } catch (error) {
      console.error('Error fetching report:', error);
      toast.error('Failed to fetch report details');
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      category_id: '',
      activity_date: '',
      content: '',
      tags: [],
      status: 'draft'
    });
    setEditingReport(null);
  };

  const openEditModal = (report) => {
    setEditingReport(report);
    setFormData({
      title: report.title || '',
      category_id: report.category_id || '',
      activity_date: report.activity_date ? report.activity_date.split('T')[0] : '',
      content: report.content || '',
      tags: report.tags || [],
      status: report.status || 'draft'
    });
    setShowReportModal(true);
  };

  const handleUpdateReport = async () => {
    try {
      const token = getToken();
      if (!token) return;

      const response = await fetch(`${API_URL}/leader-reports/reports/${editingReport.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (data.success) {
        toast.success('Report updated successfully');
        setShowReportModal(false);
        resetForm();
        fetchReports();
        fetchDashboardStats();
      } else {
        toast.error(data.message || 'Failed to update report');
      }
    } catch (error) {
      console.error('Error updating report:', error);
      toast.error('Failed to update report');
    }
  };

  const handleDeleteReport = async () => {
    try {
      const token = getToken();
      if (!token) return;

      const response = await fetch(`${API_URL}/leader-reports/reports/${deletingReport.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const data = await response.json();

      if (data.success) {
        toast.success('Report deleted successfully');
        setShowDeleteConfirm(false);
        setDeletingReport(null);
        fetchReports();
        fetchDashboardStats();
        if (activeView === 'view-report') setActiveView('reports');
      } else {
        toast.error(data.message || 'Failed to delete report');
      }
    } catch (error) {
      console.error('Error deleting report:', error);
      toast.error('Failed to delete report');
    }
  };

  // ==================== RENDER FUNCTIONS ====================

  const renderDashboard = () => (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 className="h4 fw-bold mb-0">📋 Leader Reports Dashboard</h2>
        <button
          className="btn btn-primary"
          onClick={() => {
            resetForm();
            setShowReportModal(true);
          }}
        >
          <i className="bi bi-plus-circle me-2"></i>
          New Report
        </button>
      </div>

      {stats && (
        <div className="row g-4 mb-4">
          {stats.totalReports !== undefined && (
            <>
              <div className="col-md-3">
                <div className="card border-0 shadow-sm">
                  <div className="card-body">
                    <div className="d-flex align-items-center">
                      <div className="rounded-circle bg-primary bg-opacity-10 p-3">
                        <i className="bi bi-file-earmark-text fs-4 text-primary"></i>
                      </div>
                      <div className="ms-3">
                        <p className="text-muted mb-0 small">Total Reports</p>
                        <h3 className="h4 fw-bold mb-0">{stats.totalReports}</h3>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-md-3">
                <div className="card border-0 shadow-sm">
                  <div className="card-body">
                    <div className="d-flex align-items-center">
                      <div className="rounded-circle bg-success bg-opacity-10 p-3">
                        <i className="bi bi-calendar3 fs-4 text-success"></i>
                      </div>
                      <div className="ms-3">
                        <p className="text-muted mb-0 small">This Month</p>
                        <h3 className="h4 fw-bold mb-0">{stats.reportsThisMonth}</h3>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-md-3">
                <div className="card border-0 shadow-sm">
                  <div className="card-body">
                    <div className="d-flex align-items-center">
                      <div className="rounded-circle bg-warning bg-opacity-10 p-3">
                        <i className="bi bi-hourglass-split fs-4 text-warning"></i>
                      </div>
                      <div className="ms-3">
                        <p className="text-muted mb-0 small">Pending Review</p>
                        <h3 className="h4 fw-bold mb-0">{stats.pendingReview}</h3>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-md-3">
                <div className="card border-0 shadow-sm">
                  <div className="card-body">
                    <div className="d-flex align-items-center">
                      <div className="rounded-circle" style={{ backgroundColor: '#6B8E2320', padding: '12px' }}>
                        <i className="bi bi-check-circle fs-4" style={{ color: '#6B8E23' }}></i>
                      </div>
                      <div className="ms-3">
                        <p className="text-muted mb-0 small">Approved</p>
                        <h3 className="h4 fw-bold mb-0">{stats.myApproved || 0}</h3>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}

          {stats.myTotalReports !== undefined && (
            <>
              <div className="col-md-3">
                <div className="card border-0 shadow-sm">
                  <div className="card-body">
                    <div className="d-flex align-items-center">
                      <div className="rounded-circle bg-primary bg-opacity-10 p-3">
                        <i className="bi bi-file-earmark-person fs-4 text-primary"></i>
                      </div>
                      <div className="ms-3">
                        <p className="text-muted mb-0 small">My Reports</p>
                        <h3 className="h4 fw-bold mb-0">{stats.myTotalReports}</h3>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-md-3">
                <div className="card border-0 shadow-sm">
                  <div className="card-body">
                    <div className="d-flex align-items-center">
                      <div className="rounded-circle bg-info bg-opacity-10 p-3">
                        <i className="bi bi-file-earmark-text fs-4 text-info"></i>
                      </div>
                      <div className="ms-3">
                        <p className="text-muted mb-0 small">My Drafts</p>
                        <h3 className="h4 fw-bold mb-0">{stats.myDrafts}</h3>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-md-3">
                <div className="card border-0 shadow-sm">
                  <div className="card-body">
                    <div className="d-flex align-items-center">
                      <div className="rounded-circle bg-success bg-opacity-10 p-3">
                        <i className="bi bi-calendar-check fs-4 text-success"></i>
                      </div>
                      <div className="ms-3">
                        <p className="text-muted mb-0 small">This Month</p>
                        <h3 className="h4 fw-bold mb-0">{stats.myReportsThisMonth}</h3>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-md-3">
                <div className="card border-0 shadow-sm">
                  <div className="card-body">
                    <div className="d-flex align-items-center">
                      <div className="rounded-circle" style={{ backgroundColor: '#6B8E2320', padding: '12px' }}>
                        <i className="bi bi-check-circle fs-4" style={{ color: '#6B8E23' }}></i>
                      </div>
                      <div className="ms-3">
                        <p className="text-muted mb-0 small">Approved</p>
                        <h3 className="h4 fw-bold mb-0">{stats.myApproved}</h3>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* Quick Actions */}
      <div className="row g-3">
        <div className="col-md-4">
          <div className="card border-0 shadow-sm h-100" style={{ cursor: 'pointer' }} onClick={() => setActiveView('reports')}>
            <div className="card-body text-center">
              <i className="bi bi-list-ul fs-1 text-primary mb-3"></i>
              <h5 className="fw-bold">View All Reports</h5>
              <p className="text-muted small mb-0">Browse and filter all reports</p>
            </div>
          </div>
        </div>
        <div className="col-md-4">
          <div className="card border-0 shadow-sm h-100" style={{ cursor: 'pointer' }} onClick={() => {
            setFilters({ ...filters, status: 'draft' });
            setActiveView('reports');
          }}>
            <div className="card-body text-center">
              <i className="bi bi-file-earmark-text fs-1 text-info mb-3"></i>
              <h5 className="fw-bold">My Drafts</h5>
              <p className="text-muted small mb-0">Continue working on drafts</p>
            </div>
          </div>
        </div>
        <div className="col-md-4">
          <div className="card border-0 shadow-sm h-100" style={{ cursor: 'pointer' }} onClick={() => {
            setFilters({ ...filters, status: 'submitted' });
            setActiveView('reports');
          }}>
            <div className="card-body text-center">
              <i className="bi bi-hourglass-split fs-1 text-warning mb-3"></i>
              <h5 className="fw-bold">Pending Review</h5>
              <p className="text-muted small mb-0">Reports awaiting review</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const getStatusBadge = (status) => {
    const statusConfig = {
      draft: { color: 'secondary', icon: 'file-earmark', label: 'Draft' },
      submitted: { color: 'info', icon: 'send', label: 'Submitted' },
      under_review: { color: 'warning', icon: 'hourglass-split', label: 'Under Review' },
      approved: { color: 'success', icon: 'check-circle', label: 'Approved' },
      rejected: { color: 'danger', icon: 'x-circle', label: 'Rejected' }
    };

    const config = statusConfig[status] || statusConfig.draft;

    return (
      <span className={`badge bg-${config.color}`}>
        <i className={`bi bi-${config.icon} me-1`}></i>
        {config.label}
      </span>
    );
  };

  const renderReports = () => (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 className="h4 fw-bold mb-0">📋 Reports</h2>
        <button
          className="btn btn-primary"
          onClick={() => {
            resetForm();
            setShowReportModal(true);
          }}
        >
          <i className="bi bi-plus-circle me-2"></i>
          New Report
        </button>
      </div>

      {/* Filters */}
      <div className="card border-0 shadow-sm mb-4">
        <div className="card-body">
          <div className="row g-3">
            <div className="col-md-3">
              <input
                type="text"
                className="form-control"
                placeholder="Search reports..."
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              />
            </div>
            <div className="col-md-2">
              <select
                className="form-select"
                value={filters.category}
                onChange={(e) => setFilters({ ...filters, category: e.target.value })}
              >
                <option value="">All Categories</option>
                {categories.map(cat => (
                  <option key={cat.id} value={cat.id}>{cat.name}</option>
                ))}
              </select>
            </div>
            <div className="col-md-2">
              <select
                className="form-select"
                value={filters.status}
                onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              >
                <option value="">All Status</option>
                <option value="draft">Draft</option>
                <option value="submitted">Submitted</option>
                <option value="under_review">Under Review</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
              </select>
            </div>
            <div className="col-md-2">
              <input
                type="date"
                className="form-control"
                value={filters.startDate}
                onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
                placeholder="Start Date"
              />
            </div>
            <div className="col-md-2">
              <input
                type="date"
                className="form-control"
                value={filters.endDate}
                onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
                placeholder="End Date"
              />
            </div>
            <div className="col-md-1">
              <button
                className="btn btn-outline-secondary w-100"
                onClick={() => {
                  setFilters({ category: '', status: '', search: '', startDate: '', endDate: '' });
                  setPagination({ ...pagination, page: 1 });
                }}
              >
                Clear
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Reports List */}
      {loading ? (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      ) : (
        <>
          <div className="card border-0 shadow-sm">
            <div className="card-body p-0">
              <div className="table-responsive">
                <table className="table table-hover mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>Title</th>
                      <th>Category</th>
                      <th>Activity Date</th>
                      <th>Status</th>
                      <th>Submitted By</th>
                      <th>Created</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reports.length === 0 ? (
                      <tr>
                        <td colSpan="7" className="text-center py-4 text-muted">
                          No reports found
                        </td>
                      </tr>
                    ) : (
                      reports.map(report => (
                        <tr key={report.id}>
                          <td>
                            <strong>{report.title}</strong>
                          </td>
                          <td>
                            <span className="badge" style={{ backgroundColor: report.category_color }}>
                              {report.category_name}
                            </span>
                          </td>
                          <td>{new Date(report.activity_date).toLocaleDateString()}</td>
                          <td>{getStatusBadge(report.status)}</td>
                          <td>{report.submitted_by_name}</td>
                          <td>{new Date(report.created_at).toLocaleDateString()}</td>
                          <td>
                            <div className="d-flex gap-1">
                              <button
                                className="btn btn-sm btn-outline-primary"
                                title="View"
                                onClick={() => viewReport(report.id)}
                              >
                                <i className="bi bi-eye"></i>
                              </button>
                              <button
                                className="btn btn-sm btn-outline-warning"
                                title="Edit"
                                onClick={() => openEditModal(report)}
                              >
                                <i className="bi bi-pencil"></i>
                              </button>
                              <button
                                className="btn btn-sm btn-outline-danger"
                                title="Delete"
                                onClick={() => {
                                  setDeletingReport(report);
                                  setShowDeleteConfirm(true);
                                }}
                              >
                                <i className="bi bi-trash"></i>
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Pagination + info */}
          <div className="d-flex justify-content-between align-items-center mt-4">
            <small className="text-muted">
              Showing {reports.length > 0 ? ((pagination.page - 1) * pagination.limit) + 1 : 0}–{Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} reports
            </small>
            {Math.ceil(pagination.total / pagination.limit) > 1 && (
              <nav>
                <ul className="pagination pagination-sm mb-0">
                  <li className={`page-item ${pagination.page === 1 ? 'disabled' : ''}`}>
                    <button
                      className="page-link"
                      onClick={() => setPagination({ ...pagination, page: pagination.page - 1 })}
                      disabled={pagination.page === 1}
                    >
                      <i className="bi bi-chevron-left"></i>
                    </button>
                  </li>
                  {[...Array(Math.ceil(pagination.total / pagination.limit))].map((_, i) => {
                    const pageNum = i + 1;
                    const totalPages = Math.ceil(pagination.total / pagination.limit);
                    if (totalPages <= 7 || pageNum === 1 || pageNum === totalPages ||
                      (pageNum >= pagination.page - 2 && pageNum <= pagination.page + 2)) {
                      return (
                        <li key={i} className={`page-item ${pagination.page === pageNum ? 'active' : ''}`}>
                          <button
                            className="page-link"
                            onClick={() => setPagination({ ...pagination, page: pageNum })}
                          >
                            {pageNum}
                          </button>
                        </li>
                      );
                    } else if (pageNum === pagination.page - 3 || pageNum === pagination.page + 3) {
                      return <li key={i} className="page-item disabled"><span className="page-link">…</span></li>;
                    }
                    return null;
                  })}
                  <li className={`page-item ${pagination.page >= Math.ceil(pagination.total / pagination.limit) ? 'disabled' : ''}`}>
                    <button
                      className="page-link"
                      onClick={() => setPagination({ ...pagination, page: pagination.page + 1 })}
                      disabled={pagination.page >= Math.ceil(pagination.total / pagination.limit)}
                    >
                      <i className="bi bi-chevron-right"></i>
                    </button>
                  </li>
                </ul>
              </nav>
            )}
          </div>
        </>
      )}
    </div>
  );

  const renderViewReport = () => {
    if (!selectedReport) return null;

    return (
      <div>
        <button
          className="btn btn-outline-secondary mb-4"
          onClick={() => {
            setActiveView('reports');
            setSelectedReport(null);
          }}
        >
          <i className="bi bi-arrow-left me-2"></i>
          Back to Reports
        </button>

        <div className="card border-0 shadow-sm">
          <div className="card-body">
            <div className="d-flex justify-content-between align-items-start mb-4">
              <div>
                <h2 className="h4 fw-bold mb-2">{selectedReport.title}</h2>
                <div className="d-flex gap-2 align-items-center">
                  <span className="badge" style={{ backgroundColor: selectedReport.category_color }}>
                    <i className={`bi ${selectedReport.category_icon} me-1`}></i>
                    {selectedReport.category_name}
                  </span>
                  {getStatusBadge(selectedReport.status)}
                </div>
              </div>
              <div className="text-end">
                <p className="text-muted small mb-1">Activity Date</p>
                <p className="fw-bold">{new Date(selectedReport.activity_date).toLocaleDateString()}</p>
              </div>
            </div>

            <hr />

            <div className="row mb-4">
              <div className="col-md-6">
                <p className="text-muted small mb-1">Submitted By</p>
                <p className="fw-bold">{selectedReport.submitted_by_name}</p>
              </div>
              <div className="col-md-6">
                <p className="text-muted small mb-1">Submitted On</p>
                <p className="fw-bold">
                  {selectedReport.submitted_at
                    ? new Date(selectedReport.submitted_at).toLocaleString()
                    : 'Not submitted'}
                </p>
              </div>
            </div>

            <div className="mb-4">
              <h5 className="fw-bold mb-3">Report Content</h5>
              <div className="bg-light p-4 rounded">
                <p className="mb-0" style={{ whiteSpace: 'pre-wrap' }}>{selectedReport.content}</p>
              </div>
            </div>

            {selectedReport.attachments && selectedReport.attachments.length > 0 && (
              <div className="mb-4">
                <h5 className="fw-bold mb-3">Attachments</h5>
                <div className="list-group">
                  {selectedReport.attachments.map(att => (
                    <div key={att.id} className="list-group-item d-flex justify-content-between align-items-center">
                      <div>
                        <i className="bi bi-file-earmark me-2"></i>
                        {att.original_filename}
                        <span className="text-muted small ms-2">({(att.file_size / 1024).toFixed(2)} KB)</span>
                      </div>
                      <a href={`${API_URL}${att.file_path}`} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-outline-primary">
                        <i className="bi bi-download"></i>
                      </a>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {selectedReport.comments && selectedReport.comments.length > 0 && (
              <div className="mb-4">
                <h5 className="fw-bold mb-3">Comments</h5>
                {selectedReport.comments.map(comment => (
                  <div key={comment.id} className="card mb-2">
                    <div className="card-body">
                      <div className="d-flex justify-content-between align-items-start mb-2">
                        <strong>{comment.created_by_name}</strong>
                        <small className="text-muted">{new Date(comment.created_at).toLocaleString()}</small>
                      </div>
                      <p className="mb-0">{comment.comment}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Admin Actions */}
            {(localStorage.getItem('adminRole') === 'admin' || localStorage.getItem('adminRole') === 'super_admin') && (
              <div className="mt-4 pt-4 border-top">
                <h5 className="fw-bold mb-3">Review Actions</h5>
                <div className="btn-group" role="group">
                  <button
                    className="btn btn-success"
                    onClick={() => {
                      const comment = prompt('Add an approval note (optional):');
                      handleUpdateStatus(selectedReport.id, 'approved', comment || '');
                    }}
                  >
                    <i className="bi bi-check-circle me-2"></i>
                    Approve
                  </button>
                  <button
                    className="btn btn-warning"
                    onClick={() => handleUpdateStatus(selectedReport.id, 'under_review')}
                  >
                    <i className="bi bi-hourglass-split me-2"></i>
                    Under Review
                  </button>
                  <button
                    className="btn btn-danger"
                    onClick={() => {
                      const comment = prompt('Reason for rejection (required):');
                      if (comment) {
                        handleUpdateStatus(selectedReport.id, 'rejected', comment);
                      } else {
                        toast.error('Rejection reason is required');
                      }
                    }}
                  >
                    <i className="bi bi-x-circle me-2"></i>
                    Reject
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // ==================== REPORT MODAL ====================

  const renderReportModal = () => (
    <div className={`modal fade ${showReportModal ? 'show d-block' : ''}`} style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog modal-lg">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">
              <i className={`bi ${editingReport ? 'bi-pencil-square' : 'bi-plus-circle'} me-2`}></i>
              {editingReport ? 'Edit Report' : 'Create New Report'}
            </h5>
            <button type="button" className="btn-close" onClick={() => { setShowReportModal(false); resetForm(); }}></button>
          </div>
          <div className="modal-body">
            <div className="mb-3">
              <label className="form-label fw-semibold">Title *</label>
              <input
                type="text"
                className="form-control"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="Enter report title"
              />
            </div>
            <div className="row mb-3">
              <div className="col-md-6">
                <label className="form-label fw-semibold">Category *</label>
                <select
                  className="form-select"
                  value={formData.category_id}
                  onChange={(e) => setFormData({ ...formData, category_id: e.target.value })}
                >
                  <option value="">Select Category</option>
                  {categories.map(cat => (
                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                  ))}
                </select>
              </div>
              <div className="col-md-6">
                <label className="form-label fw-semibold">Activity Date *</label>
                <input
                  type="date"
                  className="form-control"
                  value={formData.activity_date}
                  onChange={(e) => setFormData({ ...formData, activity_date: e.target.value })}
                />
              </div>
            </div>
            <div className="mb-3">
              <label className="form-label fw-semibold">Content *</label>
              <textarea
                className="form-control"
                rows="8"
                value={formData.content}
                onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                placeholder="Enter report details..."
              ></textarea>
            </div>
            <div className="mb-3">
              <label className="form-label fw-semibold">Status</label>
              <select
                className="form-select"
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
              >
                <option value="draft">Save as Draft</option>
                <option value="submitted">Submit for Review</option>
              </select>
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={() => { setShowReportModal(false); resetForm(); }}>
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={editingReport ? handleUpdateReport : handleCreateReport}
              disabled={!formData.title || !formData.category_id || !formData.activity_date || !formData.content}
            >
              <i className={`bi ${editingReport ? 'bi-check-circle' : 'bi-plus-circle'} me-2`}></i>
              {editingReport ? 'Save Changes' : (formData.status === 'draft' ? 'Save Draft' : 'Submit Report')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  const renderDeleteConfirm = () => (
    <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog modal-sm">
        <div className="modal-content">
          <div className="modal-header border-0">
            <h5 className="modal-title text-danger">
              <i className="bi bi-exclamation-triangle me-2"></i>
              Delete Report
            </h5>
          </div>
          <div className="modal-body">
            <p>Are you sure you want to delete <strong>"{deletingReport?.title}"</strong>?</p>
            <p className="text-muted small mb-0">This action cannot be undone. All attachments and comments will also be deleted.</p>
          </div>
          <div className="modal-footer border-0">
            <button
              className="btn btn-secondary btn-sm"
              onClick={() => { setShowDeleteConfirm(false); setDeletingReport(null); }}
            >
              Cancel
            </button>
            <button className="btn btn-danger btn-sm" onClick={handleDeleteReport}>
              <i className="bi bi-trash me-1"></i>
              Delete
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  // ==================== MAIN RENDER ====================

  return (
    <div className="container-fluid">
      {/* Navigation */}
      <div className="mb-4">
        <div className="d-flex gap-2">
          <button
            className={`btn d-flex align-items-center px-4 py-2 fw-medium ${activeView === 'dashboard' ? 'btn-arca-active' : 'btn-arca-inactive'}`}
            onClick={() => setActiveView('dashboard')}
            style={{
              backgroundColor: activeView === 'dashboard' ? '#6B8E23' : '#fff',
              color: activeView === 'dashboard' ? '#fff' : '#6B8E23',
              border: '2px solid #6B8E23',
              borderRadius: '8px',
              transition: 'all 0.3s ease',
              boxShadow: activeView === 'dashboard' ? '0 4px 12px rgba(107, 142, 35, 0.3)' : 'none'
            }}
            onMouseEnter={(e) => {
              if (activeView !== 'dashboard') {
                e.target.style.backgroundColor = '#f8f9fa';
              }
            }}
            onMouseLeave={(e) => {
              if (activeView !== 'dashboard') {
                e.target.style.backgroundColor = '#fff';
              }
            }}
          >
            <i className="bi bi-speedometer2 me-2"></i>
            Dashboard
          </button>
          <button
            className={`btn d-flex align-items-center px-4 py-2 fw-medium ${activeView === 'reports' ? 'btn-arca-active' : 'btn-arca-inactive'}`}
            onClick={() => setActiveView('reports')}
            style={{
              backgroundColor: activeView === 'reports' ? '#6B8E23' : '#fff',
              color: activeView === 'reports' ? '#fff' : '#6B8E23',
              border: '2px solid #6B8E23',
              borderRadius: '8px',
              transition: 'all 0.3s ease',
              boxShadow: activeView === 'reports' ? '0 4px 12px rgba(107, 142, 35, 0.3)' : 'none'
            }}
            onMouseEnter={(e) => {
              if (activeView !== 'reports') {
                e.target.style.backgroundColor = '#f8f9fa';
              }
            }}
            onMouseLeave={(e) => {
              if (activeView !== 'reports') {
                e.target.style.backgroundColor = '#fff';
              }
            }}
          >
            <i className="bi bi-list-ul me-2"></i>
            Reports
          </button>
        </div>
      </div>

      {/* Content */}
      {activeView === 'dashboard' && renderDashboard()}
      {activeView === 'reports' && renderReports()}
      {activeView === 'view-report' && renderViewReport()}

      {/* Modals */}
      {showReportModal && renderReportModal()}
      {showDeleteConfirm && deletingReport && renderDeleteConfirm()}
    </div>
  );
};

export default LeaderReportsManagement;
